import { takeLatest, put, call, takeEvery, select } from 'redux-saga/effects';
import * as authSelector from 'src/module/authentication/selector';
import { Map } from 'immutable';

import * as api from 'src/service/api/checkSheet';
import * as checkSheetActions from './action';
import * as deviceActions from '../device/action';

import toast from 'src/utils/toast';
import { getTranslation } from 'src/i18n/translations';

export function * checkSheetRoot () {
  yield takeEvery(checkSheetActions.FETCH_CHECK_SHEET_TASKS, fetchCheckSheetTasks);
  yield takeLatest(checkSheetActions.SAVE_CHECK_SHEET, saveCheckSheet);
  yield takeLatest(checkSheetActions.UPDATE_CHECK_SHEET, updateCheckSheet);
  yield takeLatest(checkSheetActions.FETCH_CHECK_SHEET, fetchCheckSheet);
  yield takeLatest(checkSheetActions.FETCH_COMMISSIONING_REPORTS, fetchCommissioningReports);
  yield takeLatest(checkSheetActions.LINK_DEVICE_TO_CHECK_SHEET, linkDeviceToCheckSheet);
  yield takeLatest(checkSheetActions.DOWNLOAD_COMMISSIONING_PDF, downloadCommissioningPdf);
  yield takeLatest(checkSheetActions.DOWNLOAD_BULK_COMMISSIONING_PDF, downloadBulkCommissioningPdf);
  yield takeLatest(checkSheetActions.DOWNLOAD_COMMISSIONING_SUMMARY_PDF, downloadCommissioningSummaryPdf);
  yield takeLatest(checkSheetActions.FETCH_REPORTS_CSV_EXPORT, fetchReportsCsvExport);
}
function * fetchCheckSheetTasks (action) {
  try {
    const tasks = yield call(api.getCheckSheetTasks, action);
    yield put(checkSheetActions.fetchCheckSheetTasksSuccess(tasks, action.checkSheetType));
  } catch (err: any) {
    yield put(checkSheetActions.fetchCheckSheetTasksFailed(Map(err)));
  }
}

function * saveCheckSheet (action) {
  try {
    const data = yield call(api.saveCheckSheet, action);
    yield put(checkSheetActions.saveCheckSheetSuccess(action.deviceId, action.checkSheetType, data));
    // add to device
    yield put(deviceActions.addDeviceCheckSheetSuccess(action.deviceId, action.checkSheetType, data.get('id')));
  } catch (err: any) {
    yield put(checkSheetActions.saveCheckSheetFailed(Map(err), action.checkSheetType));
    toast.error(getTranslation('toast.errorSavingCheckSheet'));
  }
}
function * updateCheckSheet (action) {
  try {
    const data = yield call(api.updateCheckSheet, action);
    yield put(checkSheetActions.updateCheckSheetSuccess(action.deviceId, data, action.checkSheetType));
    yield put(checkSheetActions.closeCheckSheet(action.checkSheetType));
  } catch (err: any) {
    yield put(checkSheetActions.updateCheckSheetFailed(Map(err), action.checkSheetType));
    toast.error(getTranslation('toast.errorSavingCheckSheet'));
  }
}
function * fetchCheckSheet (action) {
  try {
    const report = yield call(api.fetchCheckSheet, action);
    yield put(checkSheetActions.fetchCheckSheetSuccess( action.checkSheetType, report, action.checkSheetId));
  } catch (err: any) {
    yield put(checkSheetActions.fetchCheckSheetFailed(Map(err)));
    toast.error(getTranslation('toast.errorFetchingCheckSheet'));
  }
}

function triggerBlobDownload (response: any, filename: string, mimeType?: string) {
  const blob = mimeType ? new Blob([response], { type: mimeType }) : new Blob([response]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function safeFacilityPrefix (facilityName: string | null) {
  return facilityName ? `${facilityName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-` : '';
}

function * downloadCommissioningPdf (action: any) {
  try {
    const response = yield call(api.downloadCommissioningPdf, action);
    yield put(checkSheetActions.downloadCommissioningPdfSuccess(action.reportId));
    const deviceTypePart = action.checkSheetType.replace('commissioning-', '');
    const tag = action.deviceTag ? `-${action.deviceTag}` : '';
    triggerBlobDownload(response, `${deviceTypePart}-commissioning-report${tag}.pdf`);
  } catch (err: any) {
    yield put(checkSheetActions.downloadCommissioningPdfFailed(action.reportId));
    toast.error('Error generating commissioning PDF');
  }
}

function * downloadCommissioningSummaryPdf (action) {
  try {
    const facilityName = yield select(authSelector.getFacilityName);
    const response = yield call(api.downloadCommissioningSummaryPdf, action);
    yield put(checkSheetActions.downloadCommissioningSummaryPdfSuccess());
    triggerBlobDownload(response, `${safeFacilityPrefix(facilityName)}commissioning-summary.pdf`);
  } catch (err: any) {
    yield put(checkSheetActions.downloadCommissioningSummaryPdfFailed());
    toast.error('Error generating commissioning summary PDF');
  }
}

function * downloadBulkCommissioningPdf (action) {
  try {
    const facilityName = yield select(authSelector.getFacilityName);
    const response = yield call(api.downloadBulkCommissioningPdf, action);
    yield put(checkSheetActions.downloadBulkCommissioningPdfSuccess());
    triggerBlobDownload(response, `${safeFacilityPrefix(facilityName)}commissioning-reports.pdf`);
  } catch (err: any) {
    yield put(checkSheetActions.downloadBulkCommissioningPdfFailed());
    toast.error('Error generating commissioning PDF');
  }
}

function * fetchCommissioningReports (action) {
  try {
    const response = yield call(api.fetchCommissioningReports, action);
    yield put(checkSheetActions.fetchCommissioningReportsSuccess(
      response.get('data'),
      response.get('total'),
      response.get('page'),
      response.get('totalPages'),
    ));
  } catch (err: any) {
    toast.error(getTranslation('toast.errorFetchingCommissioningReports'));
    yield put(checkSheetActions.fetchCommissioningReportsFailed());
  }
}

function * linkDeviceToCheckSheet (action) {
  try {
    yield call(api.linkDeviceToCheckSheet, action);
    yield put(checkSheetActions.linkDeviceToCheckSheetSuccess(action.reportId, action.deviceId));
    toast.success(getTranslation('toast.deviceLinkedSuccess'));
  } catch (err: any) {
    toast.error(getTranslation('toast.errorLinkingDevice'));
    yield put(checkSheetActions.linkDeviceToCheckSheetFailed());
  }
}

function * fetchReportsCsvExport (action) {
  try {
    const facilityName = yield select(authSelector.getFacilityName);
    const response = yield call(api.fetchReportsCsvExport, action);
    yield put(checkSheetActions.fetchReportsCsvExportSuccess());
    triggerBlobDownload(response, `${safeFacilityPrefix(facilityName)}commissioning-reports.csv`, 'text/csv');
    toast.success(getTranslation('toast.csvExportSuccess'));
  } catch (err: any) {
    yield put(checkSheetActions.fetchReportsCsvExportFailed(Map(err)));
    toast.error(getTranslation('toast.csvExportFailed'));
  }
}

export default checkSheetRoot;
